import { applyCompoundOverrides, normalizeCbSlug } from './utils/applyManualCompoundOverrides';
import React, { useState, useEffect, useRef } from 'react';
import { coaValidationMessage } from './utils/coaValidationMessage';
import { validateFields } from './utils/validation';
import PageHeader from './components/PageHeader';
import DialogAlert from './components/DialogAlert';
import DialogFooter from './components/DialogFooter';
import { DividerRow, SelectField, TextField } from './components/FormFields';
import { showToastError } from './components/ToastNotifications';
import { FILE_TYPE_COA, UNITS_OF_MEASURE_OPTIONS } from './constants';
import { useAnimatedClose } from './hooks/useAnimatedClose';
import PackageLabResultsPreview from './PackageLabResultsPreview';
import { parseLabMeasurement as toOptionalCoaNumber, inferredEditorLabWeightBasis, manualLabWeightBasis, selectEditorLabWeight, projectManualLabWeight, labWeightFields, validLabWeightBasis, compoundLabWeight, selectLabPercent } from './utils/labMeasurement';
import PackageLabResultsOverrides, {
  cannabinoidOverrideKey,
  type TotalCannabinoidsDraft,
  terpeneOverrideKey,
  labOverrideKeys,
  TERPENES,
  CANNABINOIDS,
} from './PackageLabResultsOverrides';
import { normalizeCoaKeys } from './wysiwyg/coaUtils';
import { getFacilitiesByOrganization as getFacilityOptionsByOrganization } from './hooks/useFacilities';
import { uploadFileId } from './hooks/useFiles';
import { getCOAByPackageId } from './hooks/usePackages';
import { getProductsListWithStatus } from './hooks/useProducts';
import { getEditableCoaResponseRecord } from './utils/coaRecords';
import { manualCompoundField, manualCompoundFieldValue, hasManualCompoundField, manualCompoundValue, manualCompoundSource, manualOverrideState } from './utils/manualLabOverrides';

// Temporarily hide Remove COA on Edit Package without removing its implementation.
const SHOW_EDIT_PACKAGE_REMOVE_COA = false;

export type SyncCoaResult = {
  coaId: number | string | null;
  coa: Record<string, any> | null;
  quantity: number | null;
  unitOfMeasureName: string | null;
  productionBatchNumber: string | null;
};

type EditPackageDialogProps = {
  pkg: any;
  organizationId: number | string | undefined;
  onClose: () => void;
  onSave: (payload: any, exit?: boolean) => Promise<void> | void;
  onSyncCoa?: (packageId: number) => Promise<SyncCoaResult>;
  isStaged?: boolean;
  stagedCoaData?: any;
};

/** Normalize a terpene key or slug to bare lowercase alphanumeric, expanding Greek letters. */
const normalizeTpSlug = (s: string) => manualCompoundField(`lab_tp_${s}_override`)?.family.slice(3) ?? '';

const getOverridePrefix = (name: string): string => {
  const scalarOverride = Object.values(labOverrideKeys).find(
    (key) => key.value === name || key.enabled === name,
  );
  if (scalarOverride) return scalarOverride.value;
  const compoundMatch = name.match(
    /^(lab_(?:cb|tp)_.+)_(?:override|weightMgG|weightMl|weightPerUnit|weightBasis|weightPkg|weightSrv|uom|percent)$/,
  );
  if (compoundMatch) return compoundMatch[1];
  if (name.startsWith('lab_cb_') || name.startsWith('lab_tp_')) {
    // Unknown suffix: fall back to the compound family so the edit still resolves, and flag the gap.
    console.error('[EditPackageDialog] unsupported compound override key:', name);
    const suffixStart = name.lastIndexOf('_');
    if (suffixStart > 0) return name.slice(0, suffixStart);
  }
  return name;
};

/**
 * Scalar/nested deselections that must outlive re-hydration from source.
 * Enabled COA file selections are re-merged separately below.
 */
const scalarFalseMarkers = new Set<string>([
  labOverrideKeys.coaFileIds.enabled,
  labOverrideKeys.batchNumber.enabled,
  labOverrideKeys.strainName.enabled,
  labOverrideKeys.facility.enabled,
  labOverrideKeys.license.enabled,
  labOverrideKeys.testedBy.enabled,
  labOverrideKeys.laboratoryLicense.enabled,
  labOverrideKeys.testedDate.enabled,
  labOverrideKeys.cultivationDate.enabled,
  labOverrideKeys.harvestDate.enabled,
  labOverrideKeys.packagedDate.enabled,
  labOverrideKeys.expirationDate.enabled,
  labOverrideKeys.useByDate.enabled,
  labOverrideKeys.sellByDate.enabled,
]);

// Keep this list aligned with PackageService.ValidateProtectedLabFields. Display
// normalization may change aliases/presentation; it must never become source evidence.
const protectedResultFields = [
  'resultSetProvenance', 'analyteResults', 'labTestingStateName', 'ingredients', 'terpenes',
  'tests', 'totals', 'lab', 'labTests', 'batchTag', 'sourcePackage', 'sourceBatch', 'dateTested', 'testedDate',
] as const;

/**
 * Merge freshly-computed COA overrides with whatever the user had set previously
 * (batch, manually-added terpenes/cannabinoids, etc.), preserving user intent.
 */
const applyPrevPreservation = (
  fresh: Record<string, any>,
  prev: Record<string, any>,
  manualOverrides: Record<string, any>,
  authoredAmountOptions?: string,
): Record<string, any> => {
  const merged = { ...fresh };
  // Only a choice made in this editor context may override delayed hydration.
  if (authoredAmountOptions !== undefined) merged.amountOptions = authoredAmountOptions;
  // Project saved aliases into the canonical controls without rewriting the
  // stored source or its original marker keys during ordinary hydration.
  const displayManual = { ...manualOverrides };
  const compoundKeys = [...CANNABINOIDS.flatMap(key => Object.values(cannabinoidOverrideKey(key))),
    ...TERPENES.flatMap(key => Object.values(terpeneOverrideKey(key)))];
  for (const key of compoundKeys) {
    const target = manualCompoundField(key);
    if (!target) continue;
    if (target.suffix === 'override') {
      const state = manualOverrideState(manualOverrides, key);
      if (state !== undefined) displayManual[key] = state;
    } else if (!Object.prototype.hasOwnProperty.call(manualOverrides, key)) {
      const alias = Object.entries(manualOverrides).find(([name]) => {
        const candidate = manualCompoundField(name);
        return candidate?.family === target.family && candidate.suffix === target.suffix;
      });
      if (alias) displayManual[key] = alias[1];
    }
  }
  manualOverrides = displayManual;
  Object.keys(manualOverrides).forEach((k) => {
    if (k.endsWith('_override') && manualOverrides[k] === true) {
      // An enabled override represents the user's value, even when the refreshed COA has one too.
      const prefix = getOverridePrefix(k);
      Object.keys(manualOverrides).forEach((pk) => {
        if (pk === k || pk === prefix || pk.startsWith(prefix + '_')) merged[pk] = manualOverrides[pk];
      });
    }
  });
  Object.keys(manualOverrides).forEach((key) => {
    if (
      key.endsWith('_override')
      && manualOverrides[key] === false
      && (
        key.startsWith('lab_tp_')
        || key.startsWith('lab_cb_')
        || scalarFalseMarkers.has(key)
      )
    ) {
      merged[key] = false;
    }
  });
  // Preserve COA file-upload override when re-merging fresh COA data.
  if (manualOverrides[labOverrideKeys.coaFileIds.enabled] !== false
    && prev[labOverrideKeys.coaFileIds.enabled] && !merged[labOverrideKeys.coaFileIds.enabled]) {
    merged[labOverrideKeys.coaFileIds.enabled] = prev[labOverrideKeys.coaFileIds.enabled];
    merged[labOverrideKeys.coaFileIds.value] = prev[labOverrideKeys.coaFileIds.value];
    if (prev['lab_coaFileEntries'] !== undefined) {
      merged['lab_coaFileEntries'] = prev['lab_coaFileEntries'];
    }
  }
  return merged;
};

/** Current editor intent replaces fetched markers for an entire alias family. */
const mergeManualOverrides = (saved: Record<string, any>, current: Record<string, any>) => {
  const currentFamilies = new Set(Object.keys(current).map(key => manualCompoundField(key)?.family).filter(Boolean));
  const retainedSaved = Object.fromEntries(Object.entries(saved).filter(([key]) => {
    const family = manualCompoundField(key)?.family;
    return !family || !currentFamilies.has(family);
  }));
  return { ...retainedSaved, ...current };
};

/** Apply scalar overrides driven by a mapping table: if enabled+value are set, write to merged[target]. */
const applyScalarOverrides = (
  merged: Record<string, any>,
  overridesSrc: Record<string, any>,
  mappings: Array<{ enabled: string; value: string; target: string; keepSourceWhenDisabled?: boolean }>,
) => {
  mappings.forEach(({ enabled, value, target, keepSourceWhenDisabled }) => {
    if (overridesSrc[enabled] === false) {
      if (!keepSourceWhenDisabled) delete merged[target];
    } else if (overridesSrc[enabled]) {
      merged[target] = overridesSrc[value] ?? '';
    }
  });
};

/** Apply nested overrides onto merged[parent][field] (e.g. manufacturer.name). */
const applyNestedOverrides = (
  merged: Record<string, any>,
  overridesSrc: Record<string, any>,
  mappings: Array<{ enabled: string; value: string; parent: string; field: string; keepSourceWhenDisabled?: boolean }>,
) => {
  mappings.forEach(({ enabled, value, parent, field, keepSourceWhenDisabled }) => {
    if (overridesSrc[enabled] === false) {
      if (!keepSourceWhenDisabled && merged[parent]) {
        const copy = { ...merged[parent] };
        delete copy[field];
        merged[parent] = copy;
      }
    } else if (overridesSrc[enabled]) {
      merged[parent] = { ...(merged[parent] || {}), [field]: overridesSrc[value] ?? '' };
    }
  });
};

export const hydrateCannabinoidOverrides = (
  ingredients: Record<string, any>,
  persistedOverrides: Record<string, any> | undefined,
) => {
  const overrides: Record<string, any> = {};

  Object.keys(ingredients || {}).forEach((k) => {
    const ingredient = ingredients[k];
    if (!ingredient) return;
    let nameKey = k;
    const normalized = normalizeCbSlug(k);
    if (normalized === 'totalterpenes' || normalized === 'otherterpenes') return;
    if (normalized === 'totalcannabinoids') {
      nameKey = 'totalCannabinoids';
    }
    if (normalized === 'totalactivecannabinoids') {
      nameKey = 'totalActiveCannabinoids';
    }

    const keys = cannabinoidOverrideKey(nameKey);
    overrides[keys.enabled] = true;
    // Explicit primary clears must survive hydration before mixed CB/TP overrides merge.
    const percent = selectLabPercent(ingredient);
    if (percent !== undefined) overrides[keys.percent] = percent;
    const sourceWeight = compoundLabWeight(ingredient);
    const unit = sourceWeight.unit;
    if (typeof unit === 'string') overrides[keys.uom] = unit;
    const weight = selectEditorLabWeight(sourceWeight).value;
    if (weight !== undefined) overrides[keys.weight] = weight;
    const packageWeight = toOptionalCoaNumber(ingredient.weight?.pkg);
    if (packageWeight !== undefined) overrides[keys.weightPkg] = packageWeight;
    const servingWeight = toOptionalCoaNumber(ingredient.weight?.srv);
    if (servingWeight !== undefined) overrides[keys.weightSrv] = servingWeight;
  });

  Object.entries(persistedOverrides || {}).forEach(([key, value]) => {
    if (key.startsWith('lab_cb_')) overrides[key] = value;
  });

  return overrides;
};

/** Use the same selected physical Weight for staged snapshots and fetched COAs. */
const projectCannabinoidWeightControls = (
  ingredients: unknown,
  overrides: Record<string, any>,
  manualOverrides: Record<string, any>,
) => {
  for (const key of CANNABINOIDS) {
    const keys = cannabinoidOverrideKey(key);
    const compound = manualCompoundValue(ingredients, key, keys.enabled);
    const weight = projectManualLabWeight(compoundLabWeight(compound), manualOverrides, keys.enabled);
    const selected = selectEditorLabWeight(weight, manualOverrides, keys.enabled);
    if (Object.keys(compound).length || manualOverrideState(manualOverrides, keys.enabled) === true) {
      overrides[keys.weight] = selected.value ?? '';
      if (!hasManualCompoundField(manualOverrides, keys.enabled, 'uom')) {
        if (typeof weight.unit === 'string') overrides[keys.uom] = weight.unit;
        else delete overrides[keys.uom];
      }
    }
  }
};

/** Rehydrate only from the current COA plus authored intent, never stale staged display values. */
const projectTerpeneControls = (coa: Record<string, any>, overrides: Record<string, any>, manual: Record<string, any>) => {
  for (const key of TERPENES) {
    const keys = terpeneOverrideKey(key);
    // Older staged snapshots record display flags without separate manual intent.
    // Project their aliases onto canonical editor controls without authoring an edit.
    const authoredState = manualOverrideState(manual, keys.enabled);
    const enabled = authoredState ?? manualOverrideState(overrides, keys.enabled);
    if (enabled !== true) {
      if (enabled === false) overrides[keys.enabled] = false;
      continue;
    }
    if (authoredState !== true && overrides[keys.enabled] === true) continue;
    const compound = manualCompoundValue(manualCompoundSource(coa?.terpenes, coa?.ingredients, keys.enabled), key, keys.enabled);
    overrides[keys.enabled] = true;
    overrides[keys.percent] = hasManualCompoundField(manual, keys.enabled, 'percent')
      ? manualCompoundFieldValue(manual, keys.enabled, 'percent') : selectLabPercent(compound) ?? '';
    overrides[keys.weight] = hasManualCompoundField(manual, keys.enabled, 'weightmgg')
      ? manualCompoundFieldValue(manual, keys.enabled, 'weightMgG') : toOptionalCoaNumber(compoundLabWeight(compound).amount) ?? '';
  }
};

/** Convert an ISO datetime string (or date-only string) to YYYY-MM-DD for HTML date inputs. */
const toDateInputValue = (v: unknown): string => {
  if (!v || typeof v !== 'string') return '';
  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  // ISO datetime – take the date portion
  const m = v.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : v;
};

/** Populate override value+enabled pairs directly from top-level coadata fields. */
const extractCoaFieldOverrides = (
  coadata: Record<string, any>,
  overrides: Record<string, any>,
  mappings: Array<{ source: string; valueKey: string; enabledKey?: string; isDate?: boolean }>,
) => {
  mappings.forEach(({ source, valueKey, enabledKey, isDate }) => {
    if (coadata?.[source]) {
      overrides[valueKey] = isDate ? toDateInputValue(coadata[source]) : coadata[source];
      if (enabledKey) overrides[enabledKey] = true;
    }
  });
};

const readCoaObject = (value: unknown): Record<string, any> => {
  let parsed = value;
  while (typeof parsed === 'string') parsed = JSON.parse(parsed);
  // No COA/marker is a supported state; malformed JSON and scalar/array
  // payloads are not safe defaults to write back over stored customer data.
  if (parsed == null) return {};
  if (typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('COA data must be an object.');
  }
  return parsed as Record<string, any>;
};

const normalizeCoaFileIds = (values: unknown): number[] => Array.isArray(values)
  ? [...new Set<number>(values
    .filter((id: unknown) => typeof id === 'number' || (typeof id === 'string' && id.trim() !== ''))
    .map(Number)
    .filter((id: number) => Number.isSafeInteger(id) && id > 0))]
  : [];

const validateCoaFileEntries = (entries: unknown) => {
  if (entries == null) return;
  if (!Array.isArray(entries) || entries.some(entry => entry == null || typeof entry !== 'object'
    || Array.isArray(entry) || normalizeCoaFileIds([entry.fileId]).length !== 1
    || (entry.testPerformedDate != null && typeof entry.testPerformedDate !== 'string'))) {
    throw new Error('COA attachment entries must identify a usable saved file.');
  }
};

const readCoaOverrides = (value: unknown): Record<string, any> => {
  const overrides = readCoaObject(value);
  validateCoaFileEntries(overrides.lab_coaFileEntries);
  if (Object.prototype.hasOwnProperty.call(overrides, labOverrideKeys.coaFileIds.enabled)
    && typeof overrides[labOverrideKeys.coaFileIds.enabled] !== 'boolean') {
    throw new Error('A stored COA file override control must be boolean.');
  }
  if (overrides[labOverrideKeys.coaFileIds.enabled] === true
    && Object.prototype.hasOwnProperty.call(overrides, labOverrideKeys.coaFileIds.value)
    && !Array.isArray(overrides[labOverrideKeys.coaFileIds.value])) {
    throw new Error('An explicit COA file selection must be an array.');
  }
  const selection = overrides[labOverrideKeys.coaFileIds.value];
  if (overrides[labOverrideKeys.coaFileIds.enabled] === true && Array.isArray(selection)
    && selection.length > 0 && normalizeCoaFileIds(selection).length === 0) {
    throw new Error('A stored COA file selection has no usable file IDs.');
  }
  return overrides;
};

const selectCoaFileEntries = (ids: number[], entries: unknown) => ids.map((id) => {
  const entry = Array.isArray(entries)
    ? entries.find((item) => normalizeCoaFileIds([item?.fileId])[0] === id)
    : undefined;
  return { ...entry, fileId: id, testPerformedDate: entry?.testPerformedDate ?? null };
});

const readCoaFileMetadata = (record: any) => {
  if (record != null && (typeof record !== 'object' || Array.isArray(record))) {
    throw new Error('COA response must be an object.');
  }
  const readArray = (field: string): any[] => {
    const value = record?.[field];
    if (value == null) return [];
    if (!Array.isArray(value)) throw new Error(`COA ${field} must be an array.`);
    return value;
  };
  const metadata = {
    coaFiles: readArray('coaFiles'),
    coaFilesIds: readArray('coaFilesIds'),
    coaFileEntries: readArray('coaFileEntries'),
    coaFileNames: readArray('coaFileNames'),
  };
  validateCoaFileEntries(metadata.coaFileEntries);
  // An actual no-attachment record is safe; attachment metadata without a
  // usable persisted ID is not an empty selection we may write back on Save.
  if (Object.values(metadata).some(values => values.length > 0)
    && normalizeCoaFileIds(metadata.coaFilesIds).length === 0) {
    throw new Error('COA attachment metadata has no usable saved file IDs.');
  }
  return metadata;
};

const readCoaResponseRecord = (response: any) => {
  const record = getEditableCoaResponseRecord(response);
  if (record == null) {
    // The endpoint explicitly returns [] for a package without any COA.
    if (Array.isArray(response) && response.length === 0) return null;
    throw new Error('COA response record is missing.');
  }
  if (typeof record !== 'object' || Array.isArray(record)) throw new Error('COA response record is invalid.');
  const { result, Result, ...outer } = record;
  const payload = Object.prototype.hasOwnProperty.call(record, 'result') ? readCoaObject(result)
    : Object.prototype.hasOwnProperty.call(record, 'Result') ? readCoaObject(Result) : record;
  // Wrapper metadata cannot make a missing/empty explicit payload safe to save.
  if (Object.keys(payload).length === 0) throw new Error('COA response payload is empty.');
  const merged = { ...payload, ...outer };
  if (!Object.prototype.hasOwnProperty.call(merged, 'data')
    && Object.prototype.hasOwnProperty.call(merged, 'Data')) {
    merged.data = merged.Data;
    delete merged.Data;
  }
  // The API returns a COA record with Data (possibly an empty object) and an
  // identity. File-only metadata is not the endpoint's explicit [] no-COA state.
  if (!Object.prototype.hasOwnProperty.call(merged, 'data')
    && normalizeCoaFileIds([merged.id ?? merged.Id]).length === 0) {
    throw new Error('COA response has no recognizable record or data contract.');
  }
  return merged;
};

export default function EditPackageDialog({
  pkg,
  organizationId,
  onClose,
  onSave,
  onSyncCoa = undefined,
  isStaged = false,
  stagedCoaData = null,
}: EditPackageDialogProps) {
  const packageContextId = pkg?.id ?? pkg?.packageRowId;
  const defaultUnit =
    pkg?.unitOfMeasure ??
    UNITS_OF_MEASURE_OPTIONS.slice().sort((a, b) => a.label.localeCompare(b.label))[0]?.value ??
    '';

  const [form, setForm] = useState({
    id: packageContextId,
    productId: pkg.productId,
    facilityId: pkg.facilityId,
    quantity: pkg.qty ?? pkg.quantity ?? '',
    unitOfMeasure: pkg.uom ?? pkg.unit ?? pkg.unitOfMeasure ?? defaultUnit,
    active: pkg.active,
    market: null,
    selectedFacility: pkg.facilityId,
  });
  const { close, isClosing } = useAnimatedClose(onClose);
  const [errors, setErrors] = useState<Record<string, any>>({});
  const [productsListOptions, setProductListOptions] = useState([]);
  const [facilitiesListOptions, setFacilitiesListOptions] = useState([]);
  const [COAData, setCOAData] = useState<Record<string, any>>({});
  const authoritativeCoaRef = useRef<Record<string, any>>({});
  const normalizeEditableCoa = (raw: Record<string, any>) => {
    // JSON is the API contract. Detach the saved baseline from display transforms
    // and caller-owned staged data, including nested presentation objects.
    authoritativeCoaRef.current = JSON.parse(JSON.stringify(raw));
    return normalizeCoaKeys({ ...raw });
  };
  const [COAoverrides, setCOAOverrides] = useState<Record<string, any>>({});
  const [totalCannabinoidsDraft, setTotalCannabinoidsDraft] =
    useState<TotalCannabinoidsDraft | null>(null);

  /** Always holds the latest committed COAoverrides — safe to read inside async callbacks without stale-closure risk. */
  const COAoverridesRef = useRef<Record<string, any>>(COAoverrides);

  /** Every override write goes through here so the ref never lags behind state. */
  const commitOverrides = (next: Record<string, any>) => {
    COAoverridesRef.current = next;
    setCOAOverrides(next);
  };
  const manualOverridesRef = useRef<Record<string, any>>({});
  const authoredAmountOptionsRef = useRef<string | undefined>(undefined);
  const manualOverridesLoadedRef = useRef(false);
  const coaLoadPendingRef = useRef(true);
  const coaLoadFailedRef = useRef(false);
  const coaRequestGenerationRef = useRef(0);
  const operationContextRef = useRef(0);
  const savePendingRef = useRef(false);
  const syncPendingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCoaLoading, setIsCoaLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [coaFileIds, setCoaFileIds] = useState([]);
  const [coaFileEntries, setCoaFileEntries] = useState<{ fileId: number; testPerformedDate: string }[]>([]);
  const [coaFiles, setCoaFiles] = useState([]);
  const [coaFileDisplayNames, setCoaFileDisplayNames] = useState<string[]>([]);
  const [overrideCoaFileUrls, setOverrideCoaFileUrls] = useState<string[]>([]);
  const ownedCoaBlobUrlsRef = useRef(new Set<string>());
  const completedPendingUploadsRef = useRef(new Map<File, number>());
  const [pendingCoaFiles, setPendingCoaFiles] = useState<File[]>([]);
  const [pendingCoaFileDates, setPendingCoaFileDates] = useState<Record<string, string>>({});
  const [isUploadingCoaFiles, setIsUploadingCoaFiles] = useState(false);

  // Committed uploads can remain visible while another file is selected.
  // Keep their owned preview URLs alive while referenced by this context.
  useEffect(() => {
    const retained = new Set([...coaFiles, ...overrideCoaFileUrls]);
    for (const url of ownedCoaBlobUrlsRef.current) {
      if (!retained.has(url)) {
        URL.revokeObjectURL(url);
        ownedCoaBlobUrlsRef.current.delete(url);
      }
    }
  }, [coaFiles, overrideCoaFileUrls]);

  useEffect(() => {
    const ownedUrls = ownedCoaBlobUrlsRef.current;
    return () => {
      ownedUrls.forEach((u) => URL.revokeObjectURL(u));
      ownedUrls.clear();
    };
  }, []);

  const setOverrideValue = (name: string, value: any) => {
    if (name === 'amountOptions' && (value === 'Both' || value === 'Percent' || value === 'Weight')) {
      authoredAmountOptionsRef.current = value;
    }
    if (name === labOverrideKeys.coaFileIds.enabled && value === false) {
      // Disabling an unsaved upload must not leave a hidden batch to revive later.
      setPendingCoaFiles([]);
      setPendingCoaFileDates({});
      setOverrideCoaFileUrls([]);
      completedPendingUploadsRef.current.clear();
    }
    // Keep fetched COA data immutable while editing.
    // The persisted payload is rebuilt from the fetched COA plus currently-enabled overrides.
    const prefix = getOverridePrefix(name);
    const family = manualCompoundField(name)?.family;
    const retainCurrentFamily = (entries: Record<string, any>) => {
      const projected = { ...entries };
      const suffixes: Record<string, string> = { override: 'override', percent: 'percent',
        weightmgg: 'weightMgG', weightml: 'weightMl', weightperunit: 'weightPerUnit', weightbasis: 'weightBasis', weightpkg: 'weightPkg', weightsrv: 'weightSrv', uom: 'uom' };
      // Move only authored sibling values before pruning aliases. Hydrated values
      // live in a different record and never become saved intent here.
      for (const [key, stored] of Object.entries(entries)) {
        const candidate = manualCompoundField(key);
        if (!family || candidate?.family !== family) continue;
        const suffix = suffixes[candidate.suffix];
        if (!suffix) continue;
        const canonicalKey = `${prefix}_${suffix}`;
        if (!Object.prototype.hasOwnProperty.call(projected, canonicalKey)) projected[canonicalKey] = stored;
      }
      const enabled = manualOverrideState(entries, `${prefix}_override`);
      if (family && typeof enabled === 'boolean') projected[`${prefix}_override`] = enabled;
      return Object.fromEntries(Object.entries(projected).filter(([key]) =>
        !family || manualCompoundField(key)?.family !== family || key === prefix || key.startsWith(`${prefix}_`)));
    };
    // An explicit edit replaces obsolete equivalent aliases; unrelated manual
    // markers remain intact. Merely opening the editor does not clear anything.
    const nextOverrides = { ...retainCurrentFamily(COAoverridesRef.current), [name]: value };
    // Enabling Batch without typing must keep the current COA value, not blank it.
    if (
      name === labOverrideKeys.batchNumber.enabled &&
      value === true &&
      nextOverrides[labOverrideKeys.batchNumber.value] === undefined
    ) {
      nextOverrides[labOverrideKeys.batchNumber.value] = COAData.batch ?? '';
    }
    const suffix = manualCompoundField(name)?.suffix;
    const uomKey = `${prefix}_uom`;
    const pairedValues: Record<string, unknown> = {};
    let weightControlEdit = false;
    if (family?.startsWith('cb:') && manualOverrideState(nextOverrides, `${prefix}_override`) !== false) {
      const canonical = CANNABINOIDS.find(key => manualCompoundField(cannabinoidOverrideKey(key).enabled)?.family === family);
      const source = manualCompoundValue(COAData?.ingredients, canonical ?? '', `${prefix}_override`);
      const sourceWeight = projectManualLabWeight(compoundLabWeight(source), manualOverridesRef.current, `${prefix}_override`);
      const basis = manualLabWeightBasis(sourceWeight, manualOverridesRef.current, `${prefix}_override`) ?? inferredEditorLabWeightBasis(sourceWeight);
      const physical = labWeightFields[basis];
      if (suffix === 'weightmgg') {
        // Weight is a transient selected-basis control. Only its physical marker is authored.
        weightControlEdit = true;
        pairedValues[`${prefix}_${physical}`] = value;
        pairedValues[`${prefix}_weightBasis`] = basis;
      } else if (suffix === physical.toLowerCase()) {
        nextOverrides[`${prefix}_weightMgG`] = value;
        pairedValues[`${prefix}_weightBasis`] = basis;
      } else if (suffix === 'weightpkg' || suffix === 'weightsrv') {
        const sibling = suffix === 'weightpkg' ? 'pkg' : 'srv';
        // An independent sibling edit must not change the selected Weight target.
        if (inferredEditorLabWeightBasis({ ...sourceWeight, [sibling]: value }) !== basis) {
          pairedValues[`${prefix}_weightBasis`] = basis;
        }
      }
      if (suffix === 'uom') {
        // Keep the selected physical basis when a later source refresh changes its unit.
        pairedValues[`${prefix}_weightBasis`] = basis;
        // The dropdown controls the numerator; display intent carries the denominator.
        nextOverrides[uomKey] = typeof value === 'string' && typeof sourceWeight.unit === 'string' && sourceWeight.unit.includes('/')
          ? `${value.split('/')[0]}/${sourceWeight.unit.split('/')[1]}` : value;
      }
      Object.assign(nextOverrides, pairedValues);
      if (weightControlEdit) nextOverrides[`${prefix}_weightMgG`] = value;
    }
    commitOverrides(nextOverrides);
    const enabledKey = `${prefix}_override`;
    const manualUpdate =
      name === enabledKey
        ? Object.fromEntries(
            Object.entries(nextOverrides).filter(
              ([key]) => key === prefix || key.startsWith(`${prefix}_`),
            ),
          )
        : weightControlEdit ? { [enabledKey]: nextOverrides[enabledKey] } : { [name]: nextOverrides[name], [enabledKey]: nextOverrides[enabledKey] };
    manualOverridesRef.current = {
      ...retainCurrentFamily(manualOverridesRef.current),
      ...manualUpdate,
      ...pairedValues,
    };
  };

  const captureTotalCannabinoidsDraft = (draft: TotalCannabinoidsDraft) => {
    const keys = cannabinoidOverrideKey('totalCannabinoids');
    const family = manualCompoundField(keys.enabled)?.family;
    const familyFields = (fields: Record<string, any>) => Object.fromEntries(Object.entries(fields).filter(([key]) => manualCompoundField(key)?.family === family));
    const source = manualCompoundValue(COAData?.ingredients, 'totalCannabinoids', keys.enabled);
    const weight = projectManualLabWeight(compoundLabWeight(source), manualOverridesRef.current, keys.enabled);
    const basis = manualLabWeightBasis(weight, manualOverridesRef.current, keys.enabled) ?? inferredEditorLabWeightBasis(weight);
    setTotalCannabinoidsDraft({ ...draft, basis,
      physicalFieldPresent: Object.prototype.hasOwnProperty.call(weight, basis),
      physicalValue: weight[basis],
      formFields: familyFields(COAoverridesRef.current), manualFields: familyFields(manualOverridesRef.current) });
  };
  const resetTotalCannabinoidsCalculation = () => {
    const keys = cannabinoidOverrideKey('totalCannabinoids');
    const family = manualCompoundField(keys.enabled)?.family;
    const withoutFamily = (fields: Record<string, any>) => Object.fromEntries(
      Object.entries(fields).filter(([key]) => manualCompoundField(key)?.family !== family));
    const source = manualCompoundValue(COAData?.ingredients, 'totalCannabinoids', keys.enabled);
    const weight = compoundLabWeight(source);
    const prefix = keys.enabled.replace(/_override$/, '');
    const cleared: Record<string, unknown> = {};
    for (const [basis, field] of Object.entries(labWeightFields)) {
      if (Object.prototype.hasOwnProperty.call(weight, basis)
        || hasManualCompoundField(manualOverridesRef.current, keys.enabled, field)) cleared[`${prefix}_${field}`] = '';
    }
    // Normalization adds unit: undefined; only reported or authored units need clearing.
    if (Object.prototype.hasOwnProperty.call(source.weight ?? {}, 'unit')
      || Object.prototype.hasOwnProperty.call(source, 'uom')
      || hasManualCompoundField(manualOverridesRef.current, keys.enabled, 'uom')) cleared[keys.uom] = '';
    manualOverridesRef.current = { ...withoutFamily(manualOverridesRef.current), [keys.enabled]: true, ...cleared };
    commitOverrides({ ...withoutFamily(COAoverridesRef.current), [keys.enabled]: true, ...cleared });
  };

  const restoreTotalCannabinoidsDraft = (draft: TotalCannabinoidsDraft) => {
    const keys = cannabinoidOverrideKey('totalCannabinoids');
    const family = manualCompoundField(keys.enabled)?.family;
    const withoutFamily = (fields: Record<string, any>) => Object.fromEntries(Object.entries(fields).filter(([key]) => manualCompoundField(key)?.family !== family));
    const basis = validLabWeightBasis(draft.basis) ?? 'amount';
    const prefix = keys.enabled.replace(/_override$/, '');
    const value = Object.prototype.hasOwnProperty.call(draft, 'physicalValue') ? draft.physicalValue : draft.weight;
    // A legacy display fallback is not a measurement in the selected physical field.
    // Keep absence distinct from an explicit zero, null or empty authored value.
    const physicalIntent = draft.physicalFieldPresent === false ? {} : {
      [`${prefix}_weightBasis`]: basis, [`${prefix}_${labWeightFields[basis]}`]: value,
    };
    const restoredIntent = { ...draft.manualFields, [keys.enabled]: true, ...physicalIntent,
      ...(draft.percent !== undefined ? { [keys.percent]: draft.percent } : {}) };
    manualOverridesRef.current = { ...withoutFamily(manualOverridesRef.current), ...restoredIntent };
    commitOverrides({ ...withoutFamily(COAoverridesRef.current), ...draft.formFields,
      [keys.enabled]: true, [keys.weight]: draft.physicalFieldPresent === false ? draft.weight : value,
      ...(draft.physicalFieldPresent === false ? {} : { [`${prefix}_weightBasis`]: basis }) });
  };

  // Ordering is persisted by the server. Manual markers own membership, not
  // an older order that could undo an attachment or relink re-sort.
  const alignActiveCoaFiles = (overrides: Record<string, any>, ids: unknown[], entries: unknown[]) => {
    if (isStaged) return undefined;
    const freshIds = normalizeCoaFileIds(ids);
    const { enabled, value } = labOverrideKeys.coaFileIds;
    if (overrides[enabled] === true && Array.isArray(overrides[value])) {
      const selected = normalizeCoaFileIds(overrides[value]);
      const selectedSet = new Set(selected);
      const freshSet = new Set(freshIds);
      const ordered = [...freshIds.filter(id => selectedSet.has(id)), ...selected.filter(id => !freshSet.has(id))];
      const previousEntries = Array.isArray(overrides.lab_coaFileEntries) ? overrides.lab_coaFileEntries : [];
      const orderedEntries = selectCoaFileEntries(ordered, [...entries, ...previousEntries]);
      overrides[value] = ordered;
      overrides.lab_coaFileEntries = orderedEntries;
      if (manualOverridesRef.current[enabled] === true) {
        manualOverridesRef.current = { ...manualOverridesRef.current, [value]: ordered, lab_coaFileEntries: orderedEntries };
      }
    }
    return { ids: freshIds, entries };
  };

  // Extracted COA fetch so it can be re-used after a sync.
  // Preloaded COA content is used directly, while relink can still refresh its persisted file metadata.
  const fetchCOA = async (
    preloadedCoa?: any,
    refreshFileMetadata = false,
    packageIdOverride?: number,
  ) => {
    const requestGeneration = ++coaRequestGenerationRef.current;
    coaLoadPendingRef.current = true;
    coaLoadFailedRef.current = false;
    setIsCoaLoading(true);
    try {
      let coadata: any;
      let savedManualOverrides: Record<string, any> = {};
      let loadedCoaFileIds: any[] = [];
      let loadedCoaFileEntries: any[] = [];
      const packageIdForCoa =
        packageIdOverride ??
        packageContextId ??
        (Number.isFinite(Number(form?.id)) ? Number(form.id) : undefined);

      if (preloadedCoa !== undefined) {
        //use preloaded coa for staged packages
        const coaFileRecord = refreshFileMetadata
          ? readCoaResponseRecord(
              await getCOAByPackageId(organizationId, packageIdForCoa),
            )
          : pkg;
        if (requestGeneration !== coaRequestGenerationRef.current) return;
        if (refreshFileMetadata && !coaFileRecord) {
          throw new Error('Refreshed COA file metadata is missing.');
        }
        const fileMetadata = readCoaFileMetadata(coaFileRecord);
        if (refreshFileMetadata && !isStaged) manualOverridesLoadedRef.current = true;
        coadata = normalizeEditableCoa(readCoaObject(preloadedCoa));
        // Staged COAs retain their override state in _labOverrides; promoted records use manualOverrides.
        const hasSavedManualOverrides = coaFileRecord?.manualOverrides != null || coadata?._labManualOverrides != null;
        savedManualOverrides = readCoaOverrides(coaFileRecord?.manualOverrides);
        if (coadata?._labManualOverrides != null) {
          // Supports rows created before the marker moved to its own database column.
          savedManualOverrides = readCoaOverrides(coadata._labManualOverrides);
          delete coadata._labManualOverrides;
        }

        const savedOverrides = coadata._labOverrides;
        if (savedOverrides != null) {
          delete coadata._labOverrides;
          setCOAData(coadata);
          const validatedOverrides = readCoaOverrides(savedOverrides);
          // Older staged snapshots have only _labOverrides. Preserve their explicit
          // date choices; new snapshots also record manual intent (including {}).
          if (!hasSavedManualOverrides) {
            [labOverrideKeys.expirationDate, labOverrideKeys.useByDate, labOverrideKeys.sellByDate]
              .forEach(({ enabled, value }) => {
                if (typeof validatedOverrides[enabled] === 'boolean') {
                  savedManualOverrides[enabled] = validatedOverrides[enabled];
                  if (Object.prototype.hasOwnProperty.call(validatedOverrides, value)) {
                    savedManualOverrides[value] = validatedOverrides[value];
                  }
                }
              });
          }
          manualOverridesRef.current = mergeManualOverrides(savedManualOverrides, manualOverridesRef.current);
          const finalOverrides = applyPrevPreservation(
            { ...validatedOverrides }, COAoverridesRef.current, manualOverridesRef.current,
            authoredAmountOptionsRef.current,
          );
          const fileSelection = alignActiveCoaFiles(finalOverrides, fileMetadata.coaFilesIds, fileMetadata.coaFileEntries);
          projectCannabinoidWeightControls(coadata?.ingredients, finalOverrides, manualOverridesRef.current);
          projectTerpeneControls(coadata, finalOverrides, manualOverridesRef.current);
          commitOverrides(finalOverrides);
          setCoaFiles(fileMetadata.coaFiles);
          setCoaFileIds(fileMetadata.coaFilesIds);
          setCoaFileEntries(fileMetadata.coaFileEntries);
          setCoaFileDisplayNames(fileMetadata.coaFileNames);
          return { coadata, finalOverrides, fileSelection };
        }

        setCOAData(coadata);
        // Relink responses contain the generated COA but not the newly imported file metadata.
        // Refresh that metadata from the persisted COA record instead of resetting it from the
        // stale package prop that opened the dialog.
        setCoaFiles(fileMetadata.coaFiles);
        setCoaFileIds(fileMetadata.coaFilesIds);
        setCoaFileEntries(fileMetadata.coaFileEntries);
        setCoaFileDisplayNames(fileMetadata.coaFileNames);
        loadedCoaFileIds = fileMetadata.coaFilesIds;
        loadedCoaFileEntries = fileMetadata.coaFileEntries;
      } else {
        //regular package
        const rawResp = await getCOAByPackageId(organizationId, packageIdForCoa);
        if (requestGeneration !== coaRequestGenerationRef.current) return;
        manualOverridesLoadedRef.current = true;
        const latestCoaRecord = readCoaResponseRecord(rawResp);

        coadata = rawResp;
        try {
          const fileMetadata = readCoaFileMetadata(latestCoaRecord);
          setCoaFiles(fileMetadata.coaFiles);
          setCoaFileDisplayNames(fileMetadata.coaFileNames);
          setCoaFileEntries(fileMetadata.coaFileEntries);
          // Keep server IDs aligned with server filenames/URLs. The separate
          // explicit override selection remains authoritative when saving.
          setCoaFileIds(fileMetadata.coaFilesIds);
          loadedCoaFileIds = fileMetadata.coaFilesIds;
          loadedCoaFileEntries = fileMetadata.coaFileEntries;

          coadata = latestCoaRecord;

          const parsedData = readCoaObject(coadata?.data);

          const {
            data: _rawDataStr,
            manualOverrides: _manualOverrides,
            ...coaWithoutData
          } = (coadata || {}) as Record<string, any>;
          const fullCOA = normalizeEditableCoa({...coaWithoutData, ...(parsedData || {}), coaId: coadata?.id ?? coadata?.Id});
          const manualOverridesJson = latestCoaRecord?.manualOverrides ?? coadata?.manualOverrides;
          savedManualOverrides = readCoaOverrides(manualOverridesJson);
          if (fullCOA?._labManualOverrides != null) {
            // Supports rows created before the marker moved to its own database column.
            savedManualOverrides = readCoaOverrides(fullCOA._labManualOverrides);
            delete fullCOA._labManualOverrides;
          }

          setCOAData(fullCOA);
          coadata = fullCOA;
        } catch (error) {
          // Incomplete hydration must not become an editable empty/default COA.
          console.error('[EditPackageDialog] Failed to normalize COA data:', error);
          throw error;
        }
      }

      try {
        const overrides: Record<string, any> = {};

        if (coadata?.manufacturer) {
          extractCoaFieldOverrides(coadata.manufacturer, overrides, [
            {
              source: 'name',
              valueKey: labOverrideKeys.facility.value,
              enabledKey: labOverrideKeys.facility.enabled,
            },
            {
              source: 'licenseNumber',
              valueKey: labOverrideKeys.license.value,
              enabledKey: labOverrideKeys.license.enabled,
            },
          ]);
        }

        if (coadata?.lab) {
          extractCoaFieldOverrides(coadata.lab, overrides, [
            {
              source: 'name',
              valueKey: labOverrideKeys.testedBy.value,
              enabledKey: labOverrideKeys.testedBy.enabled,
            },
            {
              source: 'licenseNumber',
              valueKey: labOverrideKeys.laboratoryLicense.value,
              enabledKey: labOverrideKeys.laboratoryLicense.enabled,
            },
          ]);
        }

        if (Array.isArray(coadata?.labTests) && coadata.labTests.length > 0) {
          const first = coadata.labTests[0];
          if (first?.at) {
            overrides[labOverrideKeys.testedDate.value] = toDateInputValue(first.at);
            overrides[labOverrideKeys.testedDate.enabled] = true;
          }
        }

        extractCoaFieldOverrides(coadata, overrides, [
          { source: 'amountOptions', valueKey: 'amountOptions' },
          { source: 'batch', valueKey: labOverrideKeys.batchNumber.value, enabledKey: labOverrideKeys.batchNumber.enabled },
          { source: 'cultivationDate', valueKey: labOverrideKeys.cultivationDate.value, enabledKey: labOverrideKeys.cultivationDate.enabled, isDate: true },
          { source: 'harvestDate', valueKey: labOverrideKeys.harvestDate.value, enabledKey: labOverrideKeys.harvestDate.enabled, isDate: true },
          { source: 'dateTested', valueKey: labOverrideKeys.testedDate.value, enabledKey: labOverrideKeys.testedDate.enabled, isDate: true },
          { source: 'testedDate', valueKey: labOverrideKeys.testedDate.value, enabledKey: labOverrideKeys.testedDate.enabled, isDate: true },
          { source: 'packagedDate', valueKey: labOverrideKeys.packagedDate.value, enabledKey: labOverrideKeys.packagedDate.enabled, isDate: true },
          { source: 'expirationDate', valueKey: labOverrideKeys.expirationDate.value, enabledKey: labOverrideKeys.expirationDate.enabled, isDate: true },
          { source: 'useByDate', valueKey: labOverrideKeys.useByDate.value, enabledKey: labOverrideKeys.useByDate.enabled, isDate: true },
          { source: 'sellByDate', valueKey: labOverrideKeys.sellByDate.value, enabledKey: labOverrideKeys.sellByDate.enabled, isDate: true },
          { source: 'strainName', valueKey: labOverrideKeys.strainName.value, enabledKey: labOverrideKeys.strainName.enabled },
        ]);

        // Keep the fetched COA immutable while using the package value for the persisted override.
        const packageExpirationDate = pkg?.expirationDate;
        if (typeof packageExpirationDate === 'string' && packageExpirationDate.trim()) {
          overrides[labOverrideKeys.expirationDate.value] = toDateInputValue(packageExpirationDate);
          overrides[labOverrideKeys.expirationDate.enabled] = true;
        }

        // Restore COA file override state when file IDs are present from the server.
        // This ensures re-opening the edit dialog reflects previously uploaded COA files.
        if (loadedCoaFileIds.length > 0) {
          overrides[labOverrideKeys.coaFileIds.value] = loadedCoaFileIds;
          overrides[labOverrideKeys.coaFileIds.enabled] = true;
        }
        if (loadedCoaFileEntries.length > 0) {
          overrides['lab_coaFileEntries'] = loadedCoaFileEntries;
        }

        const ingredients = coadata?.ingredients || {};
        Object.keys(ingredients).forEach((k) => {
          const it = ingredients[k];
          if (!it) return;
          const normalized = normalizeCbSlug(k);

          if (normalized === 'totalterpenes' || normalized === 'otherterpenes') {
            const tKeys = terpeneOverrideKey(
              normalized === 'totalterpenes' ? 'Total Terpenes' : 'otherTerpenes',
            );
            overrides[tKeys.enabled] = true;
            const percent = toOptionalCoaNumber(it.percent) ?? toOptionalCoaNumber(it.weight?.percent);
            if (percent !== undefined) overrides[tKeys.percent] = percent;
            const weight = toOptionalCoaNumber(it.weight?.amount);
            if (weight !== undefined) overrides[tKeys.weight] = weight;
            return;
          }
        });

        Object.assign(overrides, hydrateCannabinoidOverrides(ingredients, coadata?._labOverrides));

        const terpenes = coadata?.terpenes || {};
        Object.keys(terpenes).forEach((k) => {
          const t = terpenes[k];
          if (!t) return;
          // Normalize ASCII-prefix keys (aPinene, bMyrcene) to Greek (alpha/beta forms)
          // so overrides align with the canonical terpene list and avoid duplicates.
          const nameKey = k
            .replace(/^a([A-Z])/, 'α$1')
            .replace(/^b([A-Z])/, 'β$1')
            .replace(/^a ([a-z])/, (_, ch) => `α${ch.toUpperCase()}`)
            .replace(/^b ([a-z])/, (_, ch) => `β${ch.toUpperCase()}`);
          const keys = terpeneOverrideKey(nameKey);
          overrides[keys.enabled] = true;
          const percent = toOptionalCoaNumber(t.percent) ?? toOptionalCoaNumber(t.weight?.percent);
          if (percent !== undefined) overrides[keys.percent] = percent;
          const weight = toOptionalCoaNumber(t.weight?.amount);
          if (weight !== undefined) overrides[keys.weight] = weight;

          const greekVariant = nameKey
            .replace(/^a([A-Z])/, 'α$1') // "aPinene" → "αPinene"
            .replace(/^b([A-Z])/, 'β$1') // "bMyrcene" → "βMyrcene"
            .replace(/^a ([a-z])/, (_, ch) => `α${ch.toUpperCase()}`) // "a pinene" → "αPinene"
            .replace(/^b ([a-z])/, (_, ch) => `β${ch.toUpperCase()}`); // "b myrcene" → "βMyrcene"
          if (greekVariant !== nameKey) {
            const keys2 = terpeneOverrideKey(greekVariant);
            overrides[keys2.enabled] = true;
            if (percent !== undefined) overrides[keys2.percent] = percent;
            if (weight !== undefined) overrides[keys2.weight] = weight;
          }
        });

        // Compute finalOverrides using the ref so we always read the latest committed
        // COAoverrides, not the stale closure value captured at render time.
        const manualOverrides = mergeManualOverrides(savedManualOverrides, manualOverridesRef.current);
        if (Object.keys(savedManualOverrides).length > 0) {
          manualOverridesRef.current = mergeManualOverrides(savedManualOverrides, manualOverridesRef.current);
        }
        const finalOverrides = applyPrevPreservation(
          { ...overrides },
          COAoverridesRef.current,
          manualOverrides,
          authoredAmountOptionsRef.current,
        );
        const fileSelection = alignActiveCoaFiles(finalOverrides, loadedCoaFileIds, loadedCoaFileEntries);
        projectCannabinoidWeightControls(ingredients, finalOverrides, manualOverrides);
        projectTerpeneControls(coadata, finalOverrides, manualOverrides);
        commitOverrides(finalOverrides);
        try {
          const unitObj = coadata?.unit || {};
          const quantityFromCOA =
            unitObj?.weight?.amount ??
            unitObj?.quantity ??
            unitObj?.amount ??
            coadata?.quantity ??
            null;
          const unitNameCandidate =
            unitObj?.weight?.unitName ||
            unitObj?.unitOfMeasureName ||
            coadata?.weightUnitOfMeasureName ||
            coadata?.quantityUnitOfMeasureName ||
            unitObj?.uom ||
            unitObj?.unit ||
            null;
          const mappedUnit = unitNameCandidate
            ? UNITS_OF_MEASURE_OPTIONS.find(
                (u) => u.label === unitNameCandidate || u.value === unitNameCandidate,
              )?.value
            : null;
          const coaUom = coadata?.uom || coadata?.unit?.weight?.unit || null;
          const mappedUnitFinal =
            mappedUnit ??
            (coaUom
              ? (UNITS_OF_MEASURE_OPTIONS.find(
                  (u) => u.value === String(coaUom) || u.label.toLowerCase() === String(coaUom).toLowerCase(),
                )?.value ?? null)
              : null);

          const newForm = {
            ...{
              id: packageContextId,
              productId: pkg.productId,
              facilityId: pkg.facilityId,
              quantity: pkg.quantity,
              unitOfMeasure: defaultUnit,
              active: pkg.active,
              market: null,
              selectedFacility: pkg.facilityId,
            },
            facilityId: coadata?.manufacturer?.name ?? pkg.facilityId ?? undefined,
            productId: coadata?.productId ?? pkg.productId ?? undefined,
            quantity:
              quantityFromCOA != null
                ? String(quantityFromCOA)
                : (pkg.qty ?? pkg.quantity ?? coadata?.unit?.quantity ?? coadata?.quantity ?? ''),
            unitOfMeasure:
              mappedUnitFinal ?? pkg.uom ?? pkg.unit ?? pkg.unitOfMeasure ?? defaultUnit,
          };

          setForm(newForm);
        } catch (error) {
          console.error('[EditPackageDialog] Failed to map COA data onto the form:', error);
          throw error;
        }
        return { coadata, finalOverrides, fileSelection };
      } catch (error) {
        console.error('[EditPackageDialog] Failed to derive COA overrides:', error);
        throw error;
      }
    } catch (err) {
      if (requestGeneration !== coaRequestGenerationRef.current) return;
      coaLoadFailedRef.current = true;
      manualOverridesLoadedRef.current = false;
      console.warn('[EditPackageDialog] fetchCOA failed', err);
    } finally {
      if (requestGeneration === coaRequestGenerationRef.current) {
        coaLoadPendingRef.current = false;
        setIsCoaLoading(false);
      }
    }
  };

  useEffect(() => {
    let cancelled = false;
    operationContextRef.current += 1;
    savePendingRef.current = false;
    syncPendingRef.current = false;
    setIsSaving(false);
    setIsSyncing(false);
    setIsUploadingCoaFiles(false);
    manualOverridesRef.current = {};
    authoredAmountOptionsRef.current = undefined;
    // A different organization/package must not inherit the previous editor's
    // manual file selection or pending upload while its own COA is loading.
    commitOverrides({});
    setCOAData({});
    authoritativeCoaRef.current = {};
    setCoaFileIds([]);
    setCoaFileEntries([]);
    setCoaFiles([]);
    setCoaFileDisplayNames([]);
    ownedCoaBlobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    ownedCoaBlobUrlsRef.current.clear();
    setOverrideCoaFileUrls([]);
    setPendingCoaFiles([]);
    completedPendingUploadsRef.current.clear();
    setPendingCoaFileDates({});
    setTotalCannabinoidsDraft(null);
    // Only a successful COA read may authorise replacing the stored marker.
    manualOverridesLoadedRef.current = false;
    setFacilitiesListOptions([]);
    setProductListOptions([]);
    async function getFacilitiesByOrganization() {
      try {
        const facilityOptions = await getFacilityOptionsByOrganization(organizationId);
        if (!cancelled) setFacilitiesListOptions(facilityOptions?.data || []);
      } catch (error) {
        if (cancelled) return;
        console.error('[EditPackageDialog] Failed to fetch facilities:', error);
        showToastError('Facility options could not be loaded.');
      }
    }

    async function getProducts() {
      try {
        const { products, complete } = await getProductsListWithStatus({
          organizationId,
          active: null,
          pageSize: 500,
          includeBrandless: true,
        });
        if (cancelled) return;
        setProductListOptions(products);
        if (!complete) {
          showToastError('Some product options could not be loaded.');
        }
      } catch (error) {
        if (cancelled) return;
        console.error('[EditPackageDialog] Failed to fetch products:', error);
        showToastError('Product options could not be loaded.');
        setProductListOptions([]);
      }
    }

    getFacilitiesByOrganization();
    getProducts();
    if (isStaged) {
      fetchCOA(stagedCoaData ?? {});
    } else {
      fetchCOA();
    }
    return () => {
      cancelled = true;
      operationContextRef.current += 1;
      coaRequestGenerationRef.current += 1;
    };
  }, [organizationId, packageContextId]);

  const schema = [
    /* no required and editable fields */
  ];

  const update = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const buildPersistedCOAData = (
    coaDataArg?: Record<string, any>,
    coaOverridesArg?: Record<string, any>,
  ) => {
    const coaDataSrc = coaDataArg ?? COAData;
    const overridesSrc = coaOverridesArg ?? COAoverrides;
    const merged: Record<string, any> = { ...coaDataSrc };

    // File-related keys must only travel via payload.coaFileIds, never inside packageCoa.
    delete merged.coaFileIds;
    delete merged.coaFilesIds;
    delete merged.coaFiles;

    if (overridesSrc.amountOptions !== undefined) {
      merged.amountOptions = overridesSrc.amountOptions;
    }

    // Strip the raw DB 'data' string to prevent nested double-encoding.
    if (typeof merged.data === 'string') delete merged.data;

    applyScalarOverrides(merged, overridesSrc, [
      {
        enabled: labOverrideKeys.batchNumber.enabled,
        value: labOverrideKeys.batchNumber.value,
        target: 'batch',
        keepSourceWhenDisabled: true,
      },
      {
        enabled: labOverrideKeys.strainName.enabled,
        value: labOverrideKeys.strainName.value,
        target: 'strainName',
        keepSourceWhenDisabled: true,
      },
      {
        enabled: labOverrideKeys.testedDate.enabled,
        value: labOverrideKeys.testedDate.value,
        target: 'dateTested',
        keepSourceWhenDisabled: true,
      },
      {
        enabled: labOverrideKeys.cultivationDate.enabled,
        value: labOverrideKeys.cultivationDate.value,
        target: 'cultivationDate',
        keepSourceWhenDisabled: true,
      },
      {
        enabled: labOverrideKeys.harvestDate.enabled,
        value: labOverrideKeys.harvestDate.value,
        target: 'harvestDate',
        keepSourceWhenDisabled: true,
      },
      {
        enabled: labOverrideKeys.packagedDate.enabled,
        value: labOverrideKeys.packagedDate.value,
        target: 'packagedDate',
        keepSourceWhenDisabled: true,
      },
      {
        enabled: labOverrideKeys.expirationDate.enabled,
        value: labOverrideKeys.expirationDate.value,
        target: 'expirationDate',
        keepSourceWhenDisabled: true,
      },
      {
        enabled: labOverrideKeys.useByDate.enabled,
        value: labOverrideKeys.useByDate.value,
        target: 'useByDate',
      },
      {
        enabled: labOverrideKeys.sellByDate.enabled,
        value: labOverrideKeys.sellByDate.value,
        target: 'sellByDate',
      },
    ]);

    // Keep the authoritative landing-page date contract aligned with date overrides.
    if (merged.rawPackageDates && typeof merged.rawPackageDates === 'object' && !Array.isArray(merged.rawPackageDates)) {
      const rawPackageDates = { ...(merged.rawPackageDates as Record<string, any>) };
      [
        labOverrideKeys.expirationDate,
        labOverrideKeys.useByDate,
        labOverrideKeys.sellByDate,
      ].forEach(({ enabled, value }) => {
        const rawKey = value.replace(/^lab_/, '');
        const normalizedKey = rawKey.toLowerCase();
        // Hydration enables controls for ordinary flat COA dates too. Only actual
        // saved/user intent may replace raw MCA values. The package-level expiration
        // is a separate persisted source explicitly supported by RID-2728/#1160.
        const hasManualOverride = typeof manualOverridesRef.current[enabled] === 'boolean';
        const hasPackageExpiration = value === labOverrideKeys.expirationDate.value
          && typeof pkg?.expirationDate === 'string' && pkg.expirationDate.trim() !== '';
        const overrideState = hasManualOverride || hasPackageExpiration ? overridesSrc[enabled] : undefined;
        if (overrideState === true || overrideState === false) {
          Object.keys(rawPackageDates).forEach((existingKey) => {
            if (existingKey.toLowerCase() === normalizedKey) {
              delete rawPackageDates[existingKey];
            }
          });
        }
        if (overrideState === true) {
          rawPackageDates[rawKey] = overridesSrc[value] ?? '';
        }
      });
      merged.rawPackageDates = rawPackageDates;
    }

    // dateTested` is the canonical key, but the COA blob may also carry the
    // Legacy (coadata. testedDate) key when fetchCOA reads from both. Always strip it so
    // it never re-derives the override on next dialog open.
    delete merged.testedDate;

    applyNestedOverrides(merged, overridesSrc, [
      {
        enabled: labOverrideKeys.facility.enabled,
        value: labOverrideKeys.facility.value,
        parent: 'manufacturer',
        field: 'name',
        keepSourceWhenDisabled: true,
      },
      {
        enabled: labOverrideKeys.license.enabled,
        value: labOverrideKeys.license.value,
        parent: 'manufacturer',
        field: 'licenseNumber',
        keepSourceWhenDisabled: true,
      },
      {
        enabled: labOverrideKeys.testedBy.enabled,
        value: labOverrideKeys.testedBy.value,
        parent: 'lab',
        field: 'name',
        keepSourceWhenDisabled: true,
      },
      {
        enabled: labOverrideKeys.laboratoryLicense.enabled,
        value: labOverrideKeys.laboratoryLicense.value,
        parent: 'lab',
        field: 'licenseNumber',
        keepSourceWhenDisabled: true,
      },
    ]);

    merged.ingredients = applyCompoundOverrides(
      merged.ingredients || {},
      overridesSrc,
      'lab_cb_',
      normalizeCbSlug,
      CANNABINOIDS,
      cannabinoidOverrideKey,
      true,
      manualOverridesRef.current,
    );
    merged.terpenes = applyCompoundOverrides(
      merged.terpenes || {},
      overridesSrc,
      'lab_tp_',
      normalizeTpSlug,
      TERPENES,
      terpeneOverrideKey,
      false,
      manualOverridesRef.current,
      merged.ingredients,
    );

    // Mirror totalTerpenes / otherTerpenes from terpenes to ingredients
    // so legacy templates resolve {{coa.ingredients.<key>.percent}}.
    const mirrorTerpeneRollup = (key: 'totalTerpenes' | 'otherTerpenes') => {
      const slug = key.toLowerCase();
      const tpKey = Object.keys(merged.terpenes || {}).find((k) => normalizeTpSlug(k) === slug);
      const ingrKey = Object.keys(merged.ingredients || {}).find((k) => normalizeCbSlug(k) === slug);
      const enabled = manualOverrideState(overridesSrc, terpeneOverrideKey(key).enabled) !== false;

      if (enabled && tpKey) {
        const next = { ...(merged.ingredients || {}) };
        if (ingrKey && ingrKey !== key) delete next[ingrKey];
        const value = merged.terpenes[tpKey];
        next[key] = value && typeof value === 'object' && !Array.isArray(value) ? { ...value } : value;
        merged.ingredients = next;
      } else if (ingrKey && merged.ingredients) {
        const next = { ...merged.ingredients };
        delete next[ingrKey];
        merged.ingredients = next;
      }
    };
    delete merged.lab_batchNumber;
    delete merged.labBatchNumber;

    // Canonicalize terpene keys to the ASCII camelCase form used by terpenesMeta
    // so duplicates merge and downstream lookups stay consistent.
    const greekToAscii = (s: string) => s.replace(/^α/, 'a').replace(/^β/, 'b').replace(/^γ/, 'g');
    const terpeneCanonical: Record<string, string> = {};
    for (const terpene of (TERPENES as readonly string[])) {
      terpeneCanonical[greekToAscii(terpene).toLowerCase()] = greekToAscii(terpene);
    }
    const dedupedTerpenes: Record<string, any> = {};
    for (const [key, value] of Object.entries(merged.terpenes || {})) {
      const lookup = greekToAscii(key).toLowerCase();
      const canonical = terpeneCanonical[lookup] || greekToAscii(key);
      dedupedTerpenes[canonical] = value && typeof value === 'object' && !Array.isArray(value)
        ? { ...(dedupedTerpenes[canonical] || {}), ...value } : value;
    }
    merged.terpenes = dedupedTerpenes;

    if (merged.ingredients) {
      for (const key of Object.keys(merged.ingredients)) {
        if (terpeneCanonical[greekToAscii(key).toLowerCase()]) {
          delete merged.ingredients[key];
        }
      }
    }

    // Restore only the legacy rollups after removing individual terpene
    // duplicates. Mirroring earlier lets that cleanup delete these totals.
    mirrorTerpeneRollup('totalTerpenes');
    mirrorTerpeneRollup('otherTerpenes');

    if (coaDataSrc?.resultSetProvenance) {
      const authoritative = authoritativeCoaRef.current;
      for (const field of protectedResultFields) {
        if (Object.prototype.hasOwnProperty.call(authoritative, field)) merged[field] = authoritative[field];
        else delete merged[field];
      }
      // Hydration and display normalization are not manual intent. Keep untouched
      // MCA fields exact, then apply only persisted/user-authored lab overrides.
      const manual = manualOverridesRef.current;
      if (Object.keys(manual).some(key => key.startsWith('lab_cb_') && key.endsWith('_override'))) {
        merged.ingredients = applyCompoundOverrides(
          merged.ingredients && typeof merged.ingredients === 'object' && !Array.isArray(merged.ingredients)
            ? merged.ingredients : {},
          manual, 'lab_cb_', normalizeCbSlug, CANNABINOIDS, cannabinoidOverrideKey, true, manual,
        );
      }
      if (Object.keys(manual).some(key => key.startsWith('lab_tp_') && key.endsWith('_override'))) {
        merged.terpenes = applyCompoundOverrides(
          merged.terpenes && typeof merged.terpenes === 'object' && !Array.isArray(merged.terpenes)
            ? merged.terpenes : {},
          manual, 'lab_tp_', normalizeTpSlug, TERPENES, terpeneOverrideKey, false, manual, merged.ingredients,
        );
        for (const key of ['totalTerpenes', 'otherTerpenes'] as const) {
          if (typeof manualOverrideState(manual, terpeneOverrideKey(key).enabled) === 'boolean') mirrorTerpeneRollup(key);
        }
      }
      applyScalarOverrides(merged, manual, [{
        enabled: labOverrideKeys.testedDate.enabled,
        value: labOverrideKeys.testedDate.value,
        target: 'dateTested',
        keepSourceWhenDisabled: true,
      }]);
      applyNestedOverrides(merged, manual, [
        { enabled: labOverrideKeys.testedBy.enabled, value: labOverrideKeys.testedBy.value,
          parent: 'lab', field: 'name', keepSourceWhenDisabled: true },
        { enabled: labOverrideKeys.laboratoryLicense.enabled, value: labOverrideKeys.laboratoryLicense.value,
          parent: 'lab', field: 'licenseNumber', keepSourceWhenDisabled: true },
      ]);
    }
    // Existing ingredient paths can be literal template bindings. Explicit terpene
    // intent must update every retained spelling after authoritative fields restore.
    // Inferred display flags alone never rename or rewrite untouched source keys.
    const sourceIngredients = authoritativeCoaRef.current?.ingredients;
    const hasSourceBindings = sourceIngredients && typeof sourceIngredients === 'object' && !Array.isArray(sourceIngredients);
    const ingredientBindings = hasSourceBindings ? sourceIngredients : {};
    const nextIngredients = { ...(merged.ingredients || {}) };
    const bindingValues = applyCompoundOverrides(
      ingredientBindings, manualOverridesRef.current, 'lab_cb_', normalizeCbSlug,
      CANNABINOIDS, cannabinoidOverrideKey, true, manualOverridesRef.current,
    );
    // Only authoritative data and validated explicit CB intent construct binding
    // values. TP-only intent cannot invent an ingredient binding or its metadata.
    for (const key of Object.keys(bindingValues)) {
      if (!Object.prototype.hasOwnProperty.call(ingredientBindings, key)) nextIngredients[key] = bindingValues[key];
    }
    // Removal also wins over a stale/inferred CB alias resurrected on reopen.
    for (const key of Object.keys(nextIngredients)) {
      const canonical = TERPENES.find(name => normalizeTpSlug(name) === normalizeTpSlug(key));
      if (!canonical) continue;
      const enabledKey = terpeneOverrideKey(canonical).enabled;
      const authored = manualOverrideState(manualOverridesRef.current, enabledKey);
      if (authored === false || (!coaDataSrc?.resultSetProvenance && authored === undefined
        && manualOverrideState(overridesSrc, enabledKey) === false)) delete nextIngredients[key];
    }
    // Preserve each binding's own source metadata and dimensions after CB intent.
    for (const bindingKey of new Set([...Object.keys(ingredientBindings), ...Object.keys(bindingValues)])) {
      const canonical = TERPENES.find(key => normalizeTpSlug(key) === normalizeTpSlug(bindingKey));
      if (!canonical) continue;
      const enabledKey = terpeneOverrideKey(canonical).enabled;
      const authored = manualOverrideState(manualOverridesRef.current, enabledKey);
      const legacyRemoval = !coaDataSrc?.resultSetProvenance && authored === undefined
        && manualOverrideState(overridesSrc, enabledKey) === false;
      if (authored === false || legacyRemoval) {
        delete nextIngredients[bindingKey];
      } else if (canonical !== 'totalTerpenes' && canonical !== 'otherTerpenes') {
        if (Object.prototype.hasOwnProperty.call(bindingValues, bindingKey)) {
          nextIngredients[bindingKey] = authored === true
            ? applyCompoundOverrides(
                { [canonical]: bindingValues[bindingKey] }, manualOverridesRef.current,
                'lab_tp_', normalizeTpSlug, TERPENES, terpeneOverrideKey, false, manualOverridesRef.current,
              )[canonical]
            : bindingValues[bindingKey];
        } else delete nextIngredients[bindingKey];
      } else if (authored === true) {
        const terpenes = merged.terpenes || {};
        const sourceKey = Object.prototype.hasOwnProperty.call(terpenes, canonical) ? canonical
          : Object.keys(terpenes).find(key => normalizeTpSlug(key) === normalizeTpSlug(canonical));
        if (sourceKey !== undefined) {
          const value = terpenes[sourceKey];
          nextIngredients[bindingKey] = value && typeof value === 'object' && !Array.isArray(value) ? { ...value } : value;
        }
      }
    }
    // Explicit CB removal deliberately produces an empty object, even when the
    // source omitted ingredients or held null. Keep the same protected-save shape.
    const hasExplicitCbIntent = Object.entries(manualOverridesRef.current).some(([key, flag]) =>
      typeof flag === 'boolean' && key.startsWith('lab_cb_') && manualCompoundField(key)?.suffix === 'override');
    if (hasSourceBindings || hasExplicitCbIntent || Object.keys(bindingValues).length > 0 || Object.keys(nextIngredients).length > 0) {
      merged.ingredients = nextIngredients;
    } else if (Object.prototype.hasOwnProperty.call(authoritativeCoaRef.current, 'ingredients')) {
      merged.ingredients = sourceIngredients;
    } else delete merged.ingredients;
    return merged;
  };

  const buildSavePayload = (
    coaDataArg?: Record<string, any>,
    overridesArg?: Record<string, any>,
    freshFiles?: { ids: number[]; entries: unknown[] },
  ) => {
    let overrides = overridesArg ?? COAoverrides;
    const persistedCOAData = buildPersistedCOAData(coaDataArg, overridesArg);
    const rawSelectedFileIds = overrides[labOverrideKeys.coaFileIds.enabled]
      && Array.isArray(overrides[labOverrideKeys.coaFileIds.value])
      ? overrides[labOverrideKeys.coaFileIds.value]
      : (freshFiles?.ids ?? (Array.isArray(coaFileIds) ? coaFileIds : []));
    const selectedFileIds = normalizeCoaFileIds(rawSelectedFileIds);
    const availableEntries = overrides[labOverrideKeys.coaFileIds.enabled]
      && Array.isArray(overrides['lab_coaFileEntries'])
      ? overrides['lab_coaFileEntries']
      : (freshFiles?.entries ?? (Array.isArray(coaFileEntries) ? coaFileEntries : []));
    // The container prefers entries over IDs, so an explicit empty/subset ID
    // selection must also constrain its entries instead of reviving old files.
    const selectedFileEntries = selectCoaFileEntries(selectedFileIds, availableEntries);

    if (overrides[labOverrideKeys.coaFileIds.enabled] === true) {
      overrides = { ...overrides, [labOverrideKeys.coaFileIds.value]: selectedFileIds, lab_coaFileEntries: selectedFileEntries };
    }
    const manualOverrides = { ...manualOverridesRef.current };
    if (manualOverrides[labOverrideKeys.coaFileIds.enabled] === true) {
      manualOverrides[labOverrideKeys.coaFileIds.value] = selectedFileIds;
      manualOverrides.lab_coaFileEntries = selectedFileEntries;
    }
    if (isStaged) {
      const cleanedOverrides = { ...overrides };
      Object.values(labOverrideKeys).forEach((k) => {
        if (cleanedOverrides[k.enabled] === false) {
          cleanedOverrides[k.value] = '';
        }
      });
      persistedCOAData._labOverrides = cleanedOverrides;
      // Keep inferred UI values distinct from explicit intent across staged reopen.
      // An empty marker is intentional; otherwise legacy hydration would treat every
      // saved _labOverrides date as user-authored on the next save.
      persistedCOAData._labManualOverrides = { ...manualOverrides };
    }
    return {
      ...form,
      //marketIds: form.markets?.filter(m => m.selected).map(m => m.id) || [],
      labOverrides: overrides,
      packageCoa: JSON.stringify(persistedCOAData),
      manualOverrides: Object.keys(manualOverrides).length > 0 ? JSON.stringify(manualOverrides) : null,
      replaceManualOverrides: manualOverridesLoadedRef.current,
      packageCoaId: persistedCOAData.coaId,
      quantity: pkg.quantity,
      facilityId: pkg.facilityId,
      productionBatchNumber: pkg.productionBatchNumber,
      packageType: pkg.packageType,
      packagedDate: pkg.packagedDate,
      unitOfMeasureName: pkg.unitOfMeasureName,
      coaFileIds: selectedFileIds,
      coaFileEntries: selectedFileEntries,
    };
  };

  const uploadPendingCoaFiles = async (requestGeneration: number): Promise<
    { fileId: number; testPerformedDate: string }[] | null
  > => {
    if (organizationId == null) return null;
    if (!COAoverridesRef.current[labOverrideKeys.coaFileIds.enabled]) return null;
    if (pendingCoaFiles.length === 0) return null;

    setIsUploadingCoaFiles(true);
    try {
      const uploadedEntries: { fileId: number; testPerformedDate: string }[] = [];
      for (const file of pendingCoaFiles) {
        if (requestGeneration !== coaRequestGenerationRef.current) return null;
        let numericFileId = completedPendingUploadsRef.current.get(file);
        if (numericFileId == null) {
          const fileId = await uploadFileId({
            organizationId,
            fileType: FILE_TYPE_COA,
            mimeType: file.type || 'application/pdf',
            file,
          });
          // An already submitted upload cannot safely be deleted here. Never
          // continue the old batch or attach it to a newly selected package.
          if (requestGeneration !== coaRequestGenerationRef.current) return null;
          [numericFileId] = normalizeCoaFileIds([fileId]);
          if (numericFileId == null) {
            showToastError('COA upload did not return a valid file ID. Please retry before saving.');
            throw new Error('Invalid COA upload file ID.');
          }
          // A later file can fail without requiring this successful upload again.
          completedPendingUploadsRef.current.set(file, numericFileId);
        }
        const date = pendingCoaFileDates[file.name] || new Date().toISOString();
        uploadedEntries.push({ fileId: numericFileId, testPerformedDate: date });
      }
      return uploadedEntries;
    } finally {
      if (requestGeneration === coaRequestGenerationRef.current) setIsUploadingCoaFiles(false);
    }
  };

  const handleSave = async () => {
    if (savePendingRef.current) {
      showToastError('Package changes are still saving. Please wait.');
      return;
    }
    if (isSyncing || syncPendingRef.current) {
      showToastError('Test results are still relinking. Please wait before saving.');
      return;
    }
    if (coaLoadPendingRef.current) {
      showToastError('COA data is still loading. Please wait before saving.');
      return;
    }
    if (coaLoadFailedRef.current) {
      showToastError(isStaged
        ? 'COA data could not be loaded. Close and reopen the staged package before saving changes.'
        : 'COA data could not be loaded. Relink test results or close and reopen the package before saving changes.');
      return;
    }
    const { errors: nextErrors, isValid } = validateFields(schema, form);
    setErrors(nextErrors);
    if (!isValid) return;
    if (pendingCoaFiles.length > 0 && COAoverridesRef.current[labOverrideKeys.coaFileIds.enabled]) {
      const missingDates = pendingCoaFiles.filter((file) => !pendingCoaFileDates[file.name]);
      if (missingDates.length > 0) {
        showToastError('Please provide a Test Performed Date for each COA file.');
        return;
      }
    }

    const saveGeneration = coaRequestGenerationRef.current;
    savePendingRef.current = true;
    setIsSaving(true);
    try {
      let overridesToSave = COAoverridesRef.current;
      const uploadedEntries = await uploadPendingCoaFiles(saveGeneration);
      if (saveGeneration !== coaRequestGenerationRef.current) return;

      if (uploadedEntries && uploadedEntries.length > 0) {
        const selectedIds = overridesToSave[labOverrideKeys.coaFileIds.enabled]
          && Array.isArray(overridesToSave[labOverrideKeys.coaFileIds.value])
          ? overridesToSave[labOverrideKeys.coaFileIds.value]
          : coaFileIds;
        const existingFileIds = normalizeCoaFileIds(selectedIds);
        const uploadedFileIds = uploadedEntries.map((entry) => entry.fileId);
        const mergedFileIds = [...new Set([...existingFileIds, ...uploadedFileIds])];
        const selectedEntries = Array.isArray(overridesToSave['lab_coaFileEntries'])
          ? overridesToSave['lab_coaFileEntries']
          : coaFileEntries;
        const existingEntries = selectCoaFileEntries(existingFileIds, selectedEntries);
        const mergedEntries = [...existingEntries, ...uploadedEntries];
        overridesToSave = {
          ...(COAoverridesRef.current || {}),
          [labOverrideKeys.coaFileIds.enabled]: true,
          [labOverrideKeys.coaFileIds.value]: mergedFileIds,
          ['lab_coaFileEntries']: mergedEntries,
        };
        commitOverrides(overridesToSave);
        // Persist the same explicit file selection that is sent to the API.
        // Otherwise reopen/relink can restore the pre-upload subset marker.
        manualOverridesRef.current = {
          ...manualOverridesRef.current,
          [labOverrideKeys.coaFileIds.enabled]: true,
          [labOverrideKeys.coaFileIds.value]: mergedFileIds,
          lab_coaFileEntries: mergedEntries,
        };
        const namesById = new Map(coaFileIds.map((id, index) => [String(id), coaFileDisplayNames[index] || '']));
        const urlsById = new Map(coaFileIds.map((id, index) => [String(id), coaFiles[index] || '']));
        uploadedEntries.forEach((entry, index) => {
          namesById.set(String(entry.fileId), pendingCoaFiles[index]?.name || '');
          urlsById.set(String(entry.fileId), overrideCoaFileUrls[index] || '');
        });
        setCoaFileIds(mergedFileIds);
        setCoaFileEntries(mergedEntries);
        setCoaFileDisplayNames(mergedFileIds.map((id) => namesById.get(String(id)) || ''));
        setCoaFiles(mergedFileIds.map((id) => urlsById.get(String(id)) || ''));
        setOverrideCoaFileUrls([]);
        setPendingCoaFiles([]);
        completedPendingUploadsRef.current.clear();
        setPendingCoaFileDates({});
      }

      if (typeof onSave === 'function') await onSave(buildSavePayload(undefined, overridesToSave));
    } catch (err) {
      console.error('[EditPackageDialog] failed to save package with uploaded COA files', err);
    } finally {
      if (saveGeneration === coaRequestGenerationRef.current) {
        savePendingRef.current = false;
        setIsSaving(false);
      }
    }
  };

  /*
   * Payload construction is centralised in buildSavePayload above.
   * Both handleSave and handleSyncCoa call it — update payload shape there.
   * */
  const handleSelectCoaOverrideFiles = (files: File[]) => {
    setPendingCoaFiles(files);
    setPendingCoaFileDates({});
    const retainedFiles = new Set(files);
    for (const file of completedPendingUploadsRef.current.keys()) {
      if (!retainedFiles.has(file)) completedPendingUploadsRef.current.delete(file);
    }

    // Create blob URLs so the preview can open the files before they are uploaded
    const blobUrls = files.map((f) => {
      const url = URL.createObjectURL(f);
      ownedCoaBlobUrlsRef.current.add(url);
      return url;
    });
    setOverrideCoaFileUrls(blobUrls);
  };

  const handleRemoveCoaFiles = (index: number) => {
    const usingCoaOverride = Boolean(
      COAoverridesRef.current[labOverrideKeys.coaFileIds.enabled] && overrideCoaFileUrls.length > 0,
    );

    if (usingCoaOverride) {
      URL.revokeObjectURL(overrideCoaFileUrls[index]);
      ownedCoaBlobUrlsRef.current.delete(overrideCoaFileUrls[index]);
      setOverrideCoaFileUrls((prev) => prev.filter((_, i) => i !== index));
      setPendingCoaFiles((prev) => prev.filter((_, i) => i !== index));
      setPendingCoaFileDates((prev) => {
        const next = { ...prev };
        const fileName = pendingCoaFiles[index]?.name;
        if (fileName) delete next[fileName];
        return next;
      });
      return;
    }

    const nextIds = coaFileIds.filter((_: any, i: number) => i !== index);
    const nextEntries = (coaFileEntries || []).filter((_: any, i: number) => i !== index);
    setCoaFiles((prev: any[]) => prev.filter((_, i) => i !== index));
    setCoaFileIds(nextIds);
    setCoaFileEntries(nextEntries);
    commitOverrides({
      ...COAoverridesRef.current,
      [labOverrideKeys.coaFileIds.enabled]: true,
      [labOverrideKeys.coaFileIds.value]: nextIds,
      ['lab_coaFileEntries']: nextEntries,
    });
  };

  const handleSyncCoa = async () => {
    if (savePendingRef.current) {
      showToastError('Package changes are still saving. Please wait before relinking.');
      return;
    }
    if (isSyncing || syncPendingRef.current) {
      showToastError('Test results are already relinking. Please wait.');
      return;
    }
    if (coaLoadPendingRef.current) {
      showToastError('COA data is still loading. Please wait before relinking.');
      return;
    }
    if (pendingCoaFiles.length > 0 && COAoverridesRef.current[labOverrideKeys.coaFileIds.enabled]) {
      showToastError('Save or remove the selected COA files before relinking test results.');
      return;
    }
    const numericId =
      packageContextId ?? (Number.isFinite(Number(form?.id)) ? Number(form.id) : null);
    const syncGeneration = coaRequestGenerationRef.current;
    const syncContext = operationContextRef.current;
    syncPendingRef.current = true;

    try {
      setIsSyncing(true);

      const pkgIdToSend = numericId != null ? Number(numericId) : Number(form?.id) || NaN;

      if (!onSyncCoa || !Number.isFinite(pkgIdToSend)) {
        console.error('[EditPackageDialog] cannot sync COA: numeric package id required');
        showToastError('Failed to relink test results. Please try again.');
        return;
      }

      /*Use the fresh Metrc COA from the sync response to update the preview.
      * The preloaded path in fetchCOA handles override preservation via applyPrevPreservation.
      */
      const syncResult: SyncCoaResult = await onSyncCoa(pkgIdToSend);
      if (syncGeneration !== coaRequestGenerationRef.current) return;
      const relinkedCoa = syncResult.coa;
      const hasInlineCoaPayload =
        relinkedCoa &&
        typeof relinkedCoa === 'object' &&
        Object.keys(relinkedCoa).some((key) => !['coaId', 'id', 'Id'].includes(String(key)));
      const fetchResult = await fetchCOA(
        hasInlineCoaPayload ? relinkedCoa : undefined,
        Boolean(hasInlineCoaPayload),
        pkgIdToSend,
      );
      if (fetchResult && typeof onSave === 'function') {
        const { coadata: freshCOA, finalOverrides: freshOverrides } = fetchResult;
        const syncCoaId = syncResult.coaId;
        const freshQty = syncResult.quantity;
        const freshUom = syncResult.unitOfMeasureName;

        if (freshQty != null) {
          update('quantity', String(freshQty));
          if (pkg) pkg.quantity = freshQty;
        }
        if (freshUom != null && freshUom !== '' && pkg) {
          pkg.unitOfMeasureName = freshUom;
        }
        if (syncCoaId && freshCOA && !freshCOA.coaId) {
          freshCOA.coaId = syncCoaId;
        }

        const savePayload = buildSavePayload(freshCOA, freshOverrides, fetchResult.fileSelection);
        // Batch is Metrc-owned: prefer the value relink just read, else leave the package's own untouched.
        if (syncResult.productionBatchNumber != null) {
          savePayload.productionBatchNumber = syncResult.productionBatchNumber;
        }
        if (freshQty != null) savePayload.quantity = freshQty;
        if (freshUom != null && freshUom !== '') savePayload.unitOfMeasureName = freshUom;
        await onSave(savePayload, false);
      }
    } catch (err) {
      if (syncContext !== operationContextRef.current) return;
      console.error('Failed to sync COA', err);
      showToastError(coaValidationMessage(err, 'Failed to relink test results. Please try again.'));
    } finally {
      if (syncContext === operationContextRef.current) {
        syncPendingRef.current = false;
        setIsSyncing(false);
      }
    }
  };

  const explicitFileSelection = COAoverrides[labOverrideKeys.coaFileIds.enabled]
    && Array.isArray(COAoverrides[labOverrideKeys.coaFileIds.value])
    ? COAoverrides[labOverrideKeys.coaFileIds.value].map(String)
    : null;
  const selectedPreviewFiles = coaFiles.filter((_, index) =>
    !explicitFileSelection || explicitFileSelection.includes(String(coaFileIds[index])),
  );
  const previewFiles = COAoverrides[labOverrideKeys.coaFileIds.enabled] && pendingCoaFiles.length > 0
    ? [...selectedPreviewFiles, ...overrideCoaFileUrls]
    : selectedPreviewFiles;

  return (
    <div
      className={`stack edit-product-page page-slide-in${isClosing ? ' page-slide-out' : ''}`}
      data-testid="edit-package"
    >
      <PageHeader
        title={isStaged ? 'Edit Staged Package' : 'Edit Package'}
        subtitle="Packages"
        actions={
          <button className="button secondary" onClick={close} data-testid="edit-package-close">
            Back
          </button>
        }
      />

      <div className="drawer-body preview-right">
        <div className="drawer-preview">
          <div data-testid="edit-package-preview">
            <PackageLabResultsPreview
              manualOverrides={manualOverridesRef.current}
              form={{
                ...form,
                ...COAoverrides,
                labResults: COAData,
                coa: COAData,
                lab: { ...COAData, labTestingStateName: COAData?.labTestingStateName || pkg?.labTestingStateName },
                facilityName: pkg?.facility,
                packageLabel: pkg?.label,
              }}
              coaFiles={previewFiles}
              showRemoveCoaButton={SHOW_EDIT_PACKAGE_REMOVE_COA ? handleRemoveCoaFiles : undefined}
            />
          </div>
        </div>

        <div className="drawer-controls">
          <div className="drawer-controls-card">
            <DialogAlert show={Object.keys(errors).length > 0} testId="edit-package-validation-error" />

            <div className="form-grid">
              <TextField
                label="Id"
                disabled={true}
                value={pkg.label}
                onChange={(e) => update('id', e.target.value)}
                placeholder="Product name"
                error={errors.name}
                testId="edit-package-label"
                errorTestId="edit-package-label-error"
              />

              <SelectField
                label="Product"
                disabled={true}
                value={form.productId}
                onChange={(e) => update('productId', e.target.value)}
                error={errors.brandId}
                testId="edit-package-product"
                errorTestId="edit-package-product-error"
              >
                <option value="" disabled>
                  Select a product
                </option>
                {Array.isArray(productsListOptions) &&
                  [...productsListOptions]
                    .sort((a, b) => {
                      const labelA = a?.name ?? a?.result?.name ?? 'Unnamed product';
                      const labelB = b?.name ?? b?.result?.name ?? 'Unnamed product';
                      return labelA.localeCompare(labelB, undefined, { sensitivity: 'base' });
                    })
                    .map((b, i) => {
                      const id = b?.id ?? b?.result?.id ?? `product-${i}`;
                      const label = b?.name ?? b?.result?.name ?? 'Unnamed product';
                      return (
                        <option key={id} value={id}>
                          {label}
                        </option>
                      );
                    })}
              </SelectField>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 12 }}>
                <TextField
                  label="Quantity"
                  disabled={true}
                  value={form.quantity}
                  onChange={(e) => update('quantity', e.target.value)}
                  style={{ width: '100%', minWidth: 0 }}
                  placeholder="—"
                  testId="edit-package-quantity"
                />

                <SelectField
                  label="Unit"
                  disabled={true}
                  value={form.unitOfMeasure}
                  onChange={(e) => update('unitOfMeasure', e.target.value)}
                  style={{ width: '100%', minWidth: 0 }}
                  testId="edit-package-uom"
                >
                  <option value="" disabled>
                    Select unit
                  </option>
                  {UNITS_OF_MEASURE_OPTIONS.map((u) => (
                    <option key={u.value} value={u.value}>
                      {u.label}
                    </option>
                  ))}
                </SelectField>
              </div>

              <SelectField
                label="Facility"
                disabled={true}
                value={form.facilityId}
                onChange={(e) => update('facilityId', e.target.value)}
                error={errors.productCategoryTypeId}
                testId="edit-package-facility"
                errorTestId="edit-package-facility-error"
              >
                {[...facilitiesListOptions]
                  .sort((a, b) =>
                    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }),
                  )
                  .map((f) => (
                    <option key={f.id} value={f.name}>
                      {f.name}
                    </option>
                  ))}
              </SelectField>

              <DividerRow />

              <div className="field full-span">
                <fieldset
                  aria-label="Lab Results Overrides"
                  data-testid="edit-package-lab-results-overrides"
                  disabled={isSaving || isSyncing}
                  style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
                >
                  <PackageLabResultsOverrides
                    filePickerContextKey={`${organizationId ?? ''}:${packageContextId ?? ''}`}
                    form={{ ...form, ...COAoverrides }}
                    coa={COAData}
                    manualOverrides={manualOverridesRef.current}
                    setValue={setOverrideValue}
                    totalCannabinoidsDraft={totalCannabinoidsDraft}
                    onTotalCannabinoidsDraftChange={captureTotalCannabinoidsDraft}
                    onRestoreTotalCannabinoidsDraft={restoreTotalCannabinoidsDraft}
                    onResetTotalCannabinoidsCalculation={resetTotalCannabinoidsCalculation}
                    onSelectCoaOverrideFiles={handleSelectCoaOverrideFiles}
                    organizationId={organizationId}
                    coaFiles={coaFiles}
                    coaFileIds={coaFileIds}
                    coaFileDisplayNames={coaFileDisplayNames}
                    pendingCoaFiles={pendingCoaFiles}
                    pendingCoaFileDates={pendingCoaFileDates}
                    onCoaFileDateChange={(fileName, date) => {
                      setPendingCoaFileDates((prev) => ({ ...prev, [fileName]: date }));
                    }}
                  />
                </fieldset>
              </div>
            </div>
            <DialogFooter>
              <button
                className="button secondary"
                onClick={close}
                data-testid="edit-package-cancel"
              >
                Cancel
              </button>
              {!isStaged && (
                <button
                  className="button subtle"
                  onClick={handleSyncCoa}
                  disabled={isSyncing || isCoaLoading || isSaving || (pendingCoaFiles.length > 0 && Boolean(COAoverrides[labOverrideKeys.coaFileIds.enabled]))}
                  data-testid="edit-package-relink-coa"
                >
                  {isSyncing ? 'Relinking…' : 'Relink Test Results'}
                </button>
              )}
              <button
                className="button"
                onClick={handleSave}
                disabled={isCoaLoading || isSyncing || isSaving || isUploadingCoaFiles}
                data-testid="edit-package-save"
              >
                {isUploadingCoaFiles ? 'Uploading Files…' : 'Save Changes'}
              </button>
            </DialogFooter>
          </div>
        </div>
      </div>
    </div>
  );
}
